import { Strophe } from 'react-strophe'
import Sizzle from 'sizzle'
import * as parsers from '../../global/messageParserHelper'
import * as xmpp from '../../global/xmppPayloadHelper'
import chatLog from '../../global/chat-log'
import { sendIQ, validateConnected } from './serverConnection'
import { Chat } from '../../global/chatSettingsConfig'

/**
 * Asks for the roster for the current authorized person.
 * https://xmpp.org/extensions/xep-0321.pdf
 */
export const getRoster = ({ connection }) => {
  chatLog('[Roster] Fetching the roster')
  const stanza = xmpp.payloadToGetRoster()
  return sendIQ({ connection, stanza, parseFn: parsers.parsePeopleFromRoster })
}

export const confirmPresenceSubscription = ({ toJid, connection }) => {
  chatLog(`[Roster] Accepting presence subscription from ${toJid}`)
  const stanza = xmpp.payloadToConfirmPresenceSubscription({ toJid })
  const parseFn = (response) => response
  return sendIQ({ connection, stanza, parseFn })
}

export const askPresenceSubscription = ({ toJid, connection }) => {
  chatLog(`[Roster] Subscribing to presence for ${toJid}`)
  const stanza = xmpp.payloadToAskPresenceSubscription({ toJid })
  const parseFn = (response) => response
  return sendIQ({ connection, stanza, parseFn })
}

/**
 * Probe for the person's presence.
 * Example response:
 * <presence xmlns='jabber:client' xml:lang='en' to='cis.user.mmmm-xxxx@chat.rootstech.org/7592806185835724111166883' from='cis.user.mmmm-yyyy@chat.rootstech.org/857909492187977920116198'>
    <x xmlns='vcard-temp:x:update'/>
    <delay from='cis.user.mmmm-yyyym@chat.rootstech.org/857909492187977920116198' stamp='2021-02-01T22:18:15.321522Z' xmlns='urn:xmpp:delay'/>
    <priority>1</priority>
  </presence>
 */
export const probePresence = ({ toJid, fromJid, connection }) => {
  return new Promise((resolve) => {
    connection.addHandler(
      (stanza) => {
        const data = { isOnline: Sizzle(`priority`, stanza)?.pop()?.textContent === '1' }
        resolve(data)

        // Remove the listener by returning false
        return false
      },
      null,
      'presence',
      null,
      null,
      Strophe.getBareJidFromJid(toJid.toLowerCase()),
      { matchBareFromJid: true }
    )

    const timeout = 2000
    const successFn = () => {}

    // If they are not online, it will fail (after the timeout), so just return false.
    const errorFn = () => {
      resolve({ isOnline: false })
    }

    const stanza = xmpp.payloadToProbePresence({ toJid, fromJid })
    validateConnected({ connection, stanza })
    connection.sendPresence(stanza, successFn, errorFn, timeout)
  })
}

/*
  Remove this person from my roster, aka unsubscribe.
*/
export const removePerson = ({ connection, toJid }) => {
  chatLog(`[Roster] Unsubscribing from person ${toJid}`)
  const stanza = xmpp.payloadToRemovePerson({ toJid })
  const parseFn = (response) => response
  return sendIQ({ connection, stanza, parseFn })
}

/*
  Add this person to my roster, aka subscribe.
*/
export const addPerson = ({ toJid, connection }) => {
  if (toJid?.includes(Chat.Settings.MUC_DOMAIN)) {
    console.error(`Should not be saving this jid to the roster: ${toJid}`)
    return Promise.reject()
  }

  chatLog(`[Roster] Adding ${toJid} to roster`)
  const stanza = xmpp.payloadToAddPerson({ toJid })
  const parseFn = (response) => response
  return sendIQ({ connection, stanza, parseFn })
}

export const addPersonAndSubscribe = async ({ toJid, connection }) => {
  const add = addPerson({ toJid, connection })
  const subscribe = askPresenceSubscription({ toJid, connection })
  return Promise.all([add, subscribe])
}

/*
  When someone adds me to their roster, a subscription notifications comes
  to me. Accept the invitation and subscribe to them too.
*/
export const autoAcceptInvite = ({ jid, connection, refetchRoster }) => {
  // 1. return subscribed
  const acceptSubscription = confirmPresenceSubscription({ toJid: jid, connection })

  // 2. add the person
  const addPersonFn = addPerson({ toJid: jid, connection })

  // 3. subscribe to them
  const askSubscription = askPresenceSubscription({ toJid: jid, connection })

  // When all done update the roster.
  Promise.all([acceptSubscription, addPersonFn, askSubscription])
    .then(refetchRoster)
    .catch((err) => {
      console.error('Something went wrong trying to auto-accept invitations', err)
    })
}
